import orderBy from 'lodash/orderBy.js';
import { FormatDateString, isValidDateFormat } from 'src/utils/DateTimeHelper';
import { get } from 'lodash';
import { BuildServerlessBookCoverImageUrl } from 'src/utils/ImageHelper';
import IMAGESIZES from 'src/constants/IMAGESIZES';
import { Markup } from 'interweave';
import MouseOverPopover from '../custom-popover/mouse-hover-popover';
import { Box, Chip } from '@mui/material';
import { TrendingDown, TrendingUp } from '@mui/icons-material';
import { Link } from 'react-router';
import { getPromotionLabelTone } from 'src/utils/promotionLabelHelper';
import { PRODUCT_TYPE_OPTIONS } from 'src/constants/options';

/* 'ebook' -> 'Ebook'. Unknown values pass through so new product types still show. */
const formatProductTypeLabel = value => {
  if (!value) return '';
  const normalized = String(value).trim().toLowerCase();
  return PRODUCT_TYPE_OPTIONS.find(option => option.value === normalized)?.label || String(value).trim();
};

const formatAwardsValue = awards => {
  if (!awards) return awards;

  if (!Array.isArray(awards)) {
    return String(awards).trim();
  }

  return awards
    .map(entry => {
      if (!entry) return '';
      if (typeof entry === 'string') return entry.trim();
      if (typeof entry !== 'object') return String(entry).trim();

      const awardName = entry.awardname ? String(entry.awardname).trim() : '';
      const awardStatus = entry.awardstatus ? String(entry.awardstatus).trim() : '';

      if (awardName && awardStatus) return `${awardName} - ${awardStatus}`;
      return awardName || awardStatus;
    })
    .filter(Boolean)
    .join(', ');
};

const normalizeFormattedValue = value => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

/* 
  Massage data for NominationResult table
*/
export const FormatNominationResultData = (data, selCampaignTypeId, selectedCampaignMongoId, selectedCountry) => {
  if (!data) return;
  let finalData =
    data &&
    data.forEach(item => {
      const countryValue = selectedCountry?.[0]?.value || 'US';
      if (item.title) item.title = `"${item.title.replace(/["']/g, '')}"`;

      if (item.bookdata) {
        //Country based data will be now under country node, to reduce amount of change on frontend, flatten country node and bring it to root level of bookdata
        let flattedBookData = Object.assign({}, item.bookdata[countryValue], item.bookdata);
        item.bookdata = flattedBookData;
        delete item.bookdata[countryValue];
        //console.log('After flatten item ', flattedBookData)
      }
      //Formatting Bisacs
      if (item.bisacs && item.bisacs?.length > 0) {
        if (Array.isArray(item.bisacs)) {
          item.bisacs = item.bisacs.map(b => String(b).trim()).join(', ');
        } else {
          item.bisacs = String(item.bisacs).trim();
        }
      }

      //Fetching only one primary authors
      if (item.authors) {
        if (Array.isArray(item.authors)) {
          let primaryAuthor = item.authors.find(x => x.role === 1);
          if (primaryAuthor) {
            item.authors = primaryAuthor.display_name;
            item.firebrand_id = primaryAuthor?.firebrand_id;
          } else {
            item.authors = '';
            item.firebrand_id = '';
          }
        } else {
          item.authors = String(item.authors);
          item.firebrand_id = '';
        }
      }

      //Formatting Awards column
      if (item.awards) {
        item.awards = formatAwardsValue(item.awards);
      }

      if (item.price) item.price = parseFloat(item.price);

      //majorCampaignsCurrentMonth and majorCampaignsPriorMonth is array with property called count, so using reduce and doing sum to display on nomination result
      if (item.bookdata && item.bookdata.ignitionBatchUplift) {
        item.bookdata.ignitionBatchUplift.majorCampaignsPriorMonth =
          item.bookdata.ignitionBatchUplift.majorCampaignsPriorMonth &&
          item.bookdata.ignitionBatchUplift.majorCampaignsPriorMonth.length > 0
            ? item.bookdata.ignitionBatchUplift.majorCampaignsPriorMonth.reduce((n, { count }) => n + count, 0)
            : 0;

        item.bookdata.ignitionBatchUplift.majorCampaignsCurrentMonth =
          item.bookdata.ignitionBatchUplift.majorCampaignsCurrentMonth &&
          item.bookdata.ignitionBatchUplift.majorCampaignsCurrentMonth.length > 0
            ? item.bookdata.ignitionBatchUplift.majorCampaignsCurrentMonth.reduce((n, { count }) => n + count, 0)
            : 0;
      }
      let { campaignTypePerformanceData } = item.bookdata || {};
      if (campaignTypePerformanceData) {
        // //Find CampaignPerformanceData by MongoId
        let details = null;
        //If it is Amazon Deals, display KDD Data
        if (selCampaignTypeId === '60' || selCampaignTypeId === 60) {
          details = campaignTypePerformanceData['5d39a4438110f800121bd66d'];
        } else details = campaignTypePerformanceData[selectedCampaignMongoId];
        if (details) {
          //Just keep same campaign performance data and delete others
          delete item.bookdata.campaignTypePerformanceData;
          item.bookdata.campaignTypePerformanceData = details;
        }
      }

      //Rank calculations. If PreviousRank is > currentRank, display negative
      // if (item.bookdata?.rank && item.bookdata.rank?.changeInRank) {
      //   if (parseFloat(item.bookdata.rank.previousRank) > parseFloat(item.bookdata.rank.currentRank)) {
      //     item.bookdata.rank.changeInRank = -item.bookdata.rank.changeInRank;
      //   }
      // }

      //Filter similarity predicted units
      if (item.bookdata?.similarity?.predictivePerformanceData) {
        let predictedData =
          item.bookdata?.similarity?.predictivePerformanceData[countryValue]?.[selectedCampaignMongoId];
        delete item.bookdata.similarity.predictivePerformanceData;
        item.bookdata.similarity.predictivePerformanceData = predictedData;
      }
    });

  //Do Post Processing Here....
  console.log('After formatting response ', finalData);
  return finalData;
};

/* 
  When adding titles to draft, promo price have default Rules as per
  https://openroadmedia.atlassian.net/browse/ORMTECH-1088 & ORMTECH-1144
  12 - ORM - Halos
  10 - ORM - 1K Sale Weekly
  9 - ORM - Daily
  58 - ORM - Rank Boost

  If no rank or follower count found for book then by default make it 0 to check with promoprice rule
*/
export const DefaultPromoPriceByDLP = (
  { dlp, rank = 0, bundle, firebrandId, bookbubCategories, bookbubFollowerCount = 0 },
  ct,
  promoPriceData,
  country
) => {
  if (!dlp) return 0;
  if (bundle === 'No' || bundle === undefined) bundle = 'Single';
  else bundle = 'Bundle';

  country = country ? country[0] : 'US';

  let rulesparams = ['minimumdlp', 'maximumdlp', 'booktype', 'author'];

  const minmaxbothDLP = (maximumdlp, minimumdlp) => (dlp >= minimumdlp && dlp <= maximumdlp ? true : false);

  const checkBundle = booktype => (booktype === bundle || booktype === 'All' ? true : false);

  const checkAuthor = author => (author.includes(firebrandId) ? true : false);

  const checkBookbubCategories = categories => bookbubCategories?.some(cat => categories?.includes(cat.id));

  //Filter by CampaignType
  let finalRules = promoPriceData.filter(x => x.campaignTypes?.some(y => y === ct));

  finalRules = finalRules.filter(x => x.countries?.some(y => y === country));

  let finalPromoPrice = [];
  if (finalRules && finalRules.length > 0) {
    finalRules.forEach(rule => {
      if (rule.rules && rule.rules.length > 0) {
        let rankArray = [];
        let follwerCountArray = [];
        let matchAllCondition = true;
        let simplified = {};
        if (rule.bookbubCategories) simplified.bookbubCategories = rule.bookbubCategories;
        rulesparams.forEach(item => {
          rule.rules.forEach(x => {
            if (x.type === 'rank') {
              let rankObj = { value: x?.value, operator: x.operator };
              rankArray.push(rankObj);
            } else if (x.type === 'followercount') {
              let followeCountObj = { value: x?.value, operator: x.operator };
              follwerCountArray.push(followeCountObj);
            } else if (item === x.type) simplified[`${item}`] = x?.value;
          });
          if (rankArray && rankArray.length > 0) simplified.rank = rankArray;
          if (follwerCountArray && follwerCountArray.length > 0) simplified.followerCount = follwerCountArray;

          rankArray = [];
          follwerCountArray = [];
        });

        if (simplified.maximumdlp && simplified.minimumdlp)
          matchAllCondition = minmaxbothDLP(simplified.maximumdlp, simplified.minimumdlp);
        if (matchAllCondition && simplified.booktype) matchAllCondition = checkBundle(simplified.booktype);

        if (matchAllCondition && simplified.rank) matchAllCondition = validateValueWithOperator(simplified.rank, rank);

        if (matchAllCondition && simplified.followerCount)
          matchAllCondition = validateValueWithOperator(simplified.followerCount, bookbubFollowerCount);
        if (matchAllCondition && simplified.author) matchAllCondition = checkAuthor(simplified.author);
        if (matchAllCondition && simplified.bookbubCategories && simplified.bookbubCategories.length > 0)
          matchAllCondition = checkBookbubCategories(simplified.bookbubCategories);

        if (matchAllCondition) finalPromoPrice.push(rule.promoprice);
      }
    });
  }
  finalPromoPrice = orderBy(finalPromoPrice, [], ['desc']);
  if (finalPromoPrice && finalPromoPrice.length > 0) return finalPromoPrice[0];
  else return null; // Return null because when returning 0 it takes default promoprice as 0 for non-downprice campaign
  //https://openroadmedia.atlassian.net/browse/ORMTECH-2767
};

// This function will compare value with its mathematical operator
const validateValueWithOperator = (data, value) => {
  let matchFound = false;
  let rule, operator;
  if (data.length === 2) {
    let gteData = data.filter(x => x.operator === '$gte');
    let lteData = data.filter(x => x.operator === '$lte');
    if (gteData[0].value <= value <= lteData[0].value) matchFound = true;
  } else {
    rule = data[0].value;
    operator = data[0].operator;
    if (operator === '$gte' && value >= rule) matchFound = true;
    if (operator === '$lte' && value <= rule) matchFound = true;
    if (operator === '$gt' && value > rule) matchFound = true;
    if (operator === '$lt' && value < rule) matchFound = true;
    if (operator === '$eq' && value === rule) matchFound = true;
  }
  return matchFound;
};

export const buildExclusionFilterObject = (filterObj, selectedCampaign) => {
  let bhFrontList = [],
    isBlocklistFilterExists = false,
    isBlocklistInclusionFilterExists = false,
    isCampaingExclusionFilterExists = false,
    isSameCampaignExclusionFilter = false,
    //Only replace value during looping
    blocklistFilters = [
      {
        field: 'blocklist',
        value: { startdate: null, enddate: null },
        operator: '$or',
        type: 'daterange',
      },
    ],
    blocklistInclusionFilters = [
      {
        field: 'blocklist',
        value: { startdate: null, enddate: null },
        operator: '$or',
        type: 'daterange',
      },
    ],
    sameCampaignExclusionFilters = [
      {
        field: 'daterange-all',
        value: { startdate: null, enddate: null },
        operator: '$or',
        type: 'daterange',
      },
    ],
    campaignFilters = [
      {
        field: 'daterange-all',
        value: { startdate: null, enddate: null },
        operator: '$or',
        type: 'daterange',
      },
    ],
    seriesExclusion = [
      {
        field: 'seriesdaterange',
        value: { startdate: null, enddate: null },
        operator: '$or',
        type: 'daterange',
      },
    ];

  //Loop through each filter and decide which is used for what
  filterObj &&
    filterObj.forEach(x => {
      x.childFilters &&
        x.childFilters.forEach(item => {
          //Checking for SeriesExclusion filters
          if (item.collectionName === 'SeriesExclusion') {
            if (item.field === 'startdate') seriesExclusion[0].value.startdate = item.value;
            if (item.field === 'enddate') seriesExclusion[0].value.enddate = item.value;
            if (item.field === 'campaigntype' && item.value && item.value.length > 0) {
              seriesExclusion.push({
                field: item.field,
                operator: item.comparisonOperator,
                type: item.type,
                value: item.value && item.value.map(x => x.value),
              });
            }
          }
          //Checking for blocklist exclusion filters
          if (item.isBlockListExclusionFilter) {
            isBlocklistFilterExists = true;
            if (item.field === 'start_date') blocklistFilters[0].value.startdate = item.value;
            if (item.field === 'end_date') blocklistFilters[0].value.enddate = item.value;
          }
          //Checking for blocklist inclusion filters
          if (item.isBlockListInclusionFilter) {
            isBlocklistInclusionFilterExists = true;
            if (item.field === 'start_date') blocklistInclusionFilters[0].value.startdate = item.value;
            if (item.field === 'end_date') blocklistInclusionFilters[0].value.enddate = item.value;
          }

          //Checking for campaign exclusion filters
          //if (item.isCampaignExclusionFilter) {
          if (item.collectionName === 'excludebookincampaigntypeanddatarange') {
            isCampaingExclusionFilterExists = true;
            if (item.field === 'startdate') campaignFilters[0].value.startdate = item.value;
            if (item.field === 'enddate') campaignFilters[0].value.enddate = item.value;
            if (item.field === 'campaigntype' && item.value && item.value.length > 0) {
              campaignFilters.push({
                field: item.field,
                operator: item.comparisonOperator,
                type: item.type,
                value: item.value && item.value.map(x => x.value),
              });
            }
          }

          //Checking for campaign exclusion filters for same campaignType
          if (item.isSameCampaignExclusionFilter) {
            isSameCampaignExclusionFilter = true;
            if (item.field === 'startdate') sameCampaignExclusionFilters[0].value.startdate = item.value;
            if (item.field === 'enddate') sameCampaignExclusionFilters[0].value.enddate = item.value;
            //It is Same Campaign, so add selected campaign type
            sameCampaignExclusionFilters.push({
              field: 'campaigntype',
              operator: '$in',
              type: 'number',
              value: [parseInt(selectedCampaign)],
            });

            if (item.field === 'promotedtitles.countries' && item.value && item.value.length > 0) {
              sameCampaignExclusionFilters.push({
                field: item.field,
                operator: item.comparisonOperator,
                type: item.type,
                value: item.value && item.value.map(x => x.value),
              });
            }
          }
        });
    });
  let finalExclusionObject = {
    bloodhoundfrontlist: { operator: 'and', filters: bhFrontList },
    blocklist: { operator: 'and', filters: isBlocklistFilterExists ? blocklistFilters : [] },
    inclusionBlocklist: { operator: 'and', filters: isBlocklistInclusionFilterExists ? blocklistInclusionFilters : [] },
    excludebookincampaigntypeanddatarange: {
      operator: 'and',
      filters: isCampaingExclusionFilterExists ? campaignFilters : [],
    },
    excludecampaigntype: {
      operator: 'and',
      filters: isSameCampaignExclusionFilter ? sameCampaignExclusionFilters : [],
    },
  };
  if (seriesExclusion[0].value.startdate != null)
    finalExclusionObject.seriesexclusionbydate = { operator: 'and', filters: seriesExclusion };
  return finalExclusionObject;
};

export const buildAdditionalColumnInfoObject = (filterObj = []) => {
  const campaignFilters = [];
  const submissionFilters = [];

  filterObj.forEach(({ isAdditionalColumnFilter, childFilters, collectionName }) => {
    if (!isAdditionalColumnFilter || !Array.isArray(childFilters)) return;

    let startdate = null;
    let enddate = null;

    childFilters.forEach(({ collectionName, field, value, comparisonOperator, type }) => {
      if (value == null) return;

      if (field === 'startdate') startdate = value;
      else if (field === 'enddate') enddate = value;
      else if (field === 'campaigntype' && Array.isArray(value) && value.length > 0) {
        if (collectionName === 'campaigngrid') {
          campaignFilters.push({
            field,
            operator: comparisonOperator,
            type,
            value: value.map(v => v.value),
          });
        } else if (collectionName === 'submission') {
          submissionFilters.push({
            field,
            operator: comparisonOperator,
            type,
            value: value.map(v => v.value),
          });
        }
      }
    });

    if (startdate || enddate) {
      if (collectionName === 'campaigngrid') {
        campaignFilters.push({
          field: 'daterange-all',
          value: { startdate, enddate },
          operator: '$or',
          type: 'daterange',
        });
      } else if (collectionName === 'submission') {
        submissionFilters.push({
          field: 'daterange-all',
          value: { startdate, enddate },
          operator: '$or',
          type: 'daterange',
        });
      }
    }
  });

  return {
    campaign: { operator: 'and', filters: campaignFilters },
    submission: { operator: 'and', filters: submissionFilters },
  };
};

// Nomination table columns formatting
// ================== staring here ===========================
export const fetchGetterValue = item => {
  if (item.field === 'publisher') return (_, row) => removeQuotation(row.publisher);
  if (item.field === 'title') return (_, row) => removeQuotation(row.title);
  if (item.field === 'masterTitle') return (_, row) => row.masterTitle.map(title => title?.title).join(',');
  if (item.field === 'amazonEditorialReview.source')
    return (_, row) =>
      row.bookdata?.amazonEditorialReview ? row.bookdata.amazonEditorialReview.map(item => item.source).join(', ') : '';
  if (item.field === 'amazonEditorialReview.review')
    return (_, row) =>
      row.bookdata?.amazonEditorialReview ? row.bookdata.amazonEditorialReview.map(item => item.review).join(', ') : '';
  if (item.type === 'date')
    return (_, row) =>
      isValidDateFormat(get(row, item.getDataKey || item.field))
        ? new Date(get(row, item.getDataKey || item.field))
        : null;
  return (_, row) => get(row, item.getDataKey || item.field);
};

const removeQuotation = value => {
  if (typeof value === 'string') {
    return value?.replace(/['"]+/g, '');
  } else return '';
};

export const fetchFormatValue = item => {
  if (item.type === 'date') return value => FormatDateString(value, 'MM/DD/YYYY');
  // Stored lowercase on the book ('ebook' | 'audio' | 'print'); title-cased for
  // display only, so grid sorting and server-side filtering still see the raw
  // value they were given.
  if (item.field === 'productType') return value => formatProductTypeLabel(value);
  else return undefined;
};

export const renderCell = item => {
  if (item.field === 'image') return getBookImage;
  if (item.field === 'title')
    return ({ row }) => {
      return <MouseOverPopover title={getFormattedData(row.title)} children={getMasterTitleHoverView([row])} />;
    };
  if (item.field === 'masterTitle')
    return ({ row }) => {
      const titles = row.masterTitle.map(title => title?.title);
      return (
        <MouseOverPopover
          title={getFormattedData(titles?.join(','))}
          children={getMasterTitleHoverView(row.masterTitle)}
        />
      );
    };
  if (item.field === 'series_name') return ({ row }) => getFormattedData(row.series_name);
  if (item.field === 'rank.changeInRank') return formatChangeInRank;
  if (item.field === 'bisacs') return ({ row }) => renderBreakWord(row.bisacs);
  if (item.field === 'milestone' || item.field === 'milestone.title') return getMilestoneEventName;

  // Synchronizer recommendation columns — color-coded chips
  if (item.field === 'synchronizer.promoRecoOrganic')
    return ({ row }) => {
      const val = get(row, item.getDataKey);
      if (!val) return '';
      const tone = getPromotionLabelTone(val);
      return <Chip label={val} size='small' color={tone} variant='outlined' />;
    };
};

export const getMasterTitleHoverView = masterTitle => {
  return (
    <Box
      sx={{
        m: 2,
      }}
    >
      {masterTitle.map((data, index) => {
        const bookCoverUrl = BuildServerlessBookCoverImageUrl(IMAGESIZES.BC_SEARCHRESULT, data.image);
        return (
          <Box key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
            <img src={bookCoverUrl} style={{ width: 60 }} alt='book' />
            {data.strapline_copy && (
              <Box
                sx={{
                  width: 200,
                }}
              >
                {getFormattedData(data.strapline_copy)}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export const getBookImage = ({ row }) => {
  let bookCoverUrl = BuildServerlessBookCoverImageUrl(IMAGESIZES.BC_THUMBNAIL, row.image);
  return (
    <a target='_blank' rel='noreferrer' href={`/book/${row.primary_isbn}`}>
      <img src={bookCoverUrl} style={{ width: 40 }} alt='book' />
    </a>
  );
};

const getMilestoneEventName = ({ row }) => {
  return row?.milestone?.map((event, index) => {
    return (
      <Link to={{ pathname: '/event', search: `?eventid=${event?._id}` }} target={'_blank'}>
        {event?.title}
        {index < row?.milestone.length - 1 ? ',  ' : ''}
      </Link>
    );
  });
};

export const getFormattedData = value => {
  let rowData = normalizeFormattedValue(value).replace(/['"]+/g, '');
  const div = document.createElement('div');
  div.innerHTML = rowData;
  rowData = div.textContent?.trim() || '';
  return <Markup content={rowData} noWrap={true} />;
};

const renderBreakWord = value => {
  return <span style={{ width: '100%', overflowWrap: 'break-word' }}>{value}</span>;
};

const formatChangeInRank = ({ row }) => {
  if (!row.bookdata?.rank?.changeInRank) return null;
  return (
    <>
      <Box
        sx={{
          mr: 1,
        }}
      >
        {row.bookdata?.rank.changeInRank}
      </Box>
      {row.bookdata?.rank?.changeInRank < 0 ? (
        <TrendingDown fontSize='small' color='success' />
      ) : (
        <TrendingUp fontSize='small' color='error' />
      )}
    </>
  );
};
// Nomination table columns formatting
// ================== End Here ===========================
